import { Avatar, Button, Stack, Typography, useTheme } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Validation } from './Validation';
import { CheckMark } from 'Images';
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowButton } from 'Modules/OTPLogin/ArrowButton';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { differenceInSeconds, format } from 'date-fns';

interface ICodeValidation {
  isValidated: boolean | null;
  isBlocked: boolean;
  setIsValidated: Function;
  validationHandler: Function;
  setValidationModal: Function;
  sendCodeAgain: Function;
  setIsBlocked: Function;
}
const OTP_CODE_EXPIRATION_MS = 5000;

export const CodeValidation = ({
  isValidated,
  isBlocked,
  setIsValidated,
  validationHandler,
  setValidationModal,
  sendCodeAgain,
  setIsBlocked,
}: ICodeValidation) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.OTP_Login
  ).phoneEmailValidation;
  const [sentCodeAgainMsg, setSentCodeAgainMsg] = useState<string | null>(null);
  const [isCodeSentAgain, setIsCodeSentAgain] = useState<boolean>(false);

  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [fiveMinutesFuture, setFiveMinutesFuture] = useState<Date>(
    new Date(
      new Date().setMinutes(
        new Date().getMinutes() + OTP_CODE_EXPIRATION_MS / 1000
      )
    )
  );
  const [timedOut, setTimedOut] = useState<boolean>(false);

  const palette = useTheme().palette;

  const initTimers = useCallback(() => {
    const countDownTimerInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(countDownTimerInterval);
  }, []);

  useEffect(() => {
    initTimers();
  }, [initTimers]);

  useEffect(() => {
    if (getTimeDiffInHHMMSS(fiveMinutesFuture, currentTime) === '00:00') {
      setTimedOut(true);
      setIsBlocked(true);
    }
  }, [currentTime, fiveMinutesFuture, setIsBlocked]);

  const sendCodeAgainHandler = () => {
    const fiveMinutesFuture = new Date(
      currentTime.setMinutes(
        currentTime.getMinutes() + OTP_CODE_EXPIRATION_MS / 1000
      )
    );
    setFiveMinutesFuture(fiveMinutesFuture);
    setCurrentTime(new Date());

    setIsCodeSentAgain(true);
    setSentCodeAgainMsg(dictionary.codeSentAgain);
    setIsValidated(null);
    setIsBlocked(false);
    sendCodeAgain();
    setTimeout(() => {
      setIsCodeSentAgain(false);
      setSentCodeAgainMsg(null);
    }, OTP_CODE_EXPIRATION_MS);
  };

  const getTimeDiffInHHMMSS = (date1: Date, date2: Date) => {
    const diffInSeconds = Math.abs(differenceInSeconds(date2, date1));
    return format(new Date(diffInSeconds * 1000), 'mm:ss');
  };

  const timer = getTimeDiffInHHMMSS(fiveMinutesFuture, currentTime);

  return (
    <>
      <Stack gap={'10px'} marginBottom={'10px'} alignItems={'center'}>
        <Typography variant={'h2'}>{dictionary.whatCode}</Typography>
        {!isBlocked && (
          <Typography
            variant={'body3'}
            aria-live={timer.includes('00') && !timedOut ? 'polite' : undefined}
            role='timer'
          >
            {timedOut
              ? dictionary.codeTimerEnd
              : `${dictionary.codeTimer} ${timer}`}
          </Typography>
        )}
      </Stack>

      {isValidated && (
        <Avatar
          alt=''
          src={CheckMark}
          sx={{ width: '120px', height: '100px' }}
        />
      )}
      <Validation
        isValidated={isValidated}
        setIsValidated={setIsValidated}
        validationHandler={validationHandler}
        isCodeSentAgain={isCodeSentAgain}
        isBlocked={isBlocked}
      />
      <Stack gap={isDesktop ? '64px' : '56px'} alignItems={'center'}>
        <Stack gap={'10px'} alignItems={'center'}>
          <Typography variant={'body3'} aria-live='assertive'>
            {dictionary.didntGetCode}{' '}
            <Button
              variant='textBtn'
              sx={{
                p: '2px',
                fontSize: isDesktop ? '20px' : '18px',
              }}
              onClick={() => sendCodeAgainHandler()}
              onKeyDown={(e: React.KeyboardEvent) => onEnterOrEscapeKeyDown(e)}
              tabIndex={0}
            >
              {dictionary.sendAgain}
            </Button>
          </Typography>
        </Stack>
        {sentCodeAgainMsg && (
          <Typography
            sx={{
              mt: '-40px',
              mb: '24px',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 600,
              fontFamily: 'inherit',
              letterSpacing: '1px',
              color: '#1552E8',
            }}
            role='alert'
          >
            {sentCodeAgainMsg}
          </Typography>
        )}
        <Stack
          maxWidth={isDesktop ? '237px' : '208px'}
          height={isDesktop ? '56px' : '44px'}
        >
          <ArrowButton
            text={dictionary.backwards}
            buttonVariant={'secondary1'}
            arrowColor={palette.primary.main}
            typographyVariant={'body4'}
            arrowPosition={'right'}
            onClick={() => setValidationModal(false)}
          />
        </Stack>
      </Stack>
    </>
  );
};
